import { FC } from 'react'

/**
 * Skeleton component props
 */
interface SkeletonProps {
  /** Width of the skeleton (Tailwind class or CSS value) */
  className?: string
  /** Variant determines the shape */
  variant?: 'text' | 'circular' | 'rectangular'
  /** Height in pixels or Tailwind class (e.g., 'h-4') */
  height?: string | number
  /** Width in pixels or Tailwind class (e.g., 'w-full') */
  width?: string | number
}

/**
 * Skeleton - Loading placeholder with pulse animation
 *
 * Provides visual feedback while content is loading.
 * Uses the Sand UI design system colors for consistency.
 *
 * @example
 * ```tsx
 * // Text line skeleton
 * <Skeleton variant="text" className="w-3/4" />
 *
 * // Circular avatar skeleton
 * <Skeleton variant="circular" width={40} height={40} />
 *
 * // Rectangular card skeleton
 * <Skeleton variant="rectangular" height={120} className="w-full" />
 * ```
 */
export const Skeleton: FC<SkeletonProps> = ({
  className = '',
  variant = 'text',
  height,
  width
}) => {
  const baseClasses = 'animate-pulse bg-sand-200 dark:bg-[#3A3A3A]'

  const variantClasses = {
    text: 'rounded h-4',
    circular: 'rounded-full',
    rectangular: 'rounded-card'
  }

  const style: React.CSSProperties = {}

  if (typeof height === 'number') {
    style.height = `${height}px`
  }

  if (typeof width === 'number') {
    style.width = `${width}px`
  }

  // Handle string height/width that look like Tailwind classes
  const heightClass = typeof height === 'string' ? height : ''
  const widthClass = typeof width === 'string' ? width : ''

  return (
    <div
      className={`${baseClasses} ${variantClasses[variant]} ${heightClass} ${widthClass} ${className}`}
      style={Object.keys(style).length > 0 ? style : undefined}
      role="status"
      aria-label="Loading..."
    />
  )
}

/**
 * SkeletonText - Multiple lines of text skeleton
 */
export const SkeletonText: FC<{ lines?: number; className?: string }> = ({
  lines = 3,
  className = ''
}) => (
  <div className={`space-y-2 ${className}`} role="status" aria-label="Loading text...">
    {Array.from({ length: lines }).map((_, i) => (
      <Skeleton
        key={i}
        variant="text"
        className={i === lines - 1 ? 'w-2/3' : 'w-full'}
      />
    ))}
  </div>
)

/**
 * SkeletonCard - Card-shaped skeleton for content cards
 */
export const SkeletonCard: FC<{ className?: string }> = ({ className = '' }) => (
  <div
    className={`rounded-card bg-white dark:bg-[#2A2A2A] shadow-soft-md p-4 ${className}`}
    role="status"
    aria-label="Loading card..."
  >
    <div className="flex items-start gap-3">
      <Skeleton variant="circular" width={40} height={40} />
      <div className="flex-1 space-y-2">
        <Skeleton variant="text" className="w-1/3 h-4" />
        <Skeleton variant="text" className="w-2/3 h-3" />
      </div>
    </div>
    <div className="mt-4 space-y-2">
      <Skeleton variant="text" className="w-full" />
      <Skeleton variant="text" className="w-full" />
      <Skeleton variant="text" className="w-4/5" />
    </div>
  </div>
)

/**
 * SkeletonConversationList - Skeleton for sidebar conversation list
 */
export const SkeletonConversationList: FC<{ count?: number }> = ({ count = 5 }) => (
  <div className="space-y-1 px-2" role="status" aria-label="Loading conversations...">
    {Array.from({ length: count }).map((_, i) => (
      <div key={i} className="flex items-center gap-3 p-2 rounded-lg">
        <Skeleton variant="circular" width={32} height={32} />
        <div className="flex-1 space-y-1.5">
          <Skeleton variant="text" className={`h-3 ${i % 2 === 0 ? 'w-3/4' : 'w-1/2'}`} />
          <Skeleton variant="text" className="h-2 w-1/3" />
        </div>
      </div>
    ))}
  </div>
)

/**
 * SkeletonMessage - Skeleton for chat messages
 */
export const SkeletonMessage: FC<{ isUser?: boolean }> = ({ isUser = false }) => (
  <div
    className={`max-w-3xl p-5 rounded-message shadow-soft-md bg-white dark:bg-[#2A2A2A] ${isUser ? 'ml-auto' : ''}`}
    role="status"
    aria-label="Loading message..."
  >
    {!isUser && (
      <div className="flex items-center gap-2 mb-3">
        <Skeleton variant="circular" width={24} height={24} />
        <Skeleton variant="text" className="w-20 h-3" />
      </div>
    )}
    <div className="space-y-2">
      <Skeleton variant="text" className="w-full" />
      <Skeleton variant="text" className="w-full" />
      <Skeleton variant="text" className="w-3/4" />
    </div>
  </div>
)
